import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket, CheckCircle, ArrowRight, FileText, Calendar,
  Users, Handshake, ClipboardText, EnvelopeSimple, Phone,
  Download, Upload, Sparkle, Clock, CaretRight, Buildings
} from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ClientOnboardingPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Organization Info
    organizationName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    website: "",
    taxId: "",
    
    // Step 2: Key Contacts
    primaryContact: "",
    primaryEmail: "",
    primaryPhone: "",
    primaryRole: "",
    billingContact: "",
    billingEmail: "",
    
    // Step 3: Organization Profile
    censusAverage: "",
    employeeCount: "",
    yearsInOperation: "",
    accreditation: "",
    emrSystem: "",
    
    // Step 4: Project Details
    engagementType: "",
    primaryGoals: [],
    timeline: "",
    budget: "",
    additionalNotes: ""
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [
    { num: 1, title: "Organization Info", icon: Buildings },
    { num: 2, title: "Key Contacts", icon: Users },
    { num: 3, title: "Organization Profile", icon: ClipboardText },
    { num: 4, title: "Project Details", icon: Rocket }
  ];

  const engagementTypes = [
    "Survey Readiness Assessment",
    "QAPI Program Development",
    "Operations Optimization",
    "Census Growth Strategy",
    "Leadership Advisory",
    "AI System Implementation",
    "Custom Project"
  ];

  const goalOptions = [
    "Improve survey readiness",
    "Enhance QAPI program",
    "Increase census/referrals",
    "Reduce staff turnover",
    "Improve operational efficiency",
    "Implement AI/technology solutions",
    "Develop leadership team",
    "Achieve accreditation"
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleGoalToggle = (goal) => {
    const current = formData.primaryGoals;
    if (current.includes(goal)) {
      handleChange("primaryGoals", current.filter(g => g !== goal));
    } else {
      handleChange("primaryGoals", [...current, goal]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/client-onboarding`, {
        ...formData,
        token,
        submittedAt: new Date().toISOString()
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Submission failed:", err);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.organizationName && formData.city && formData.state;
      case 2:
        return formData.primaryContact && formData.primaryEmail;
      case 3:
        return formData.censusAverage;
      case 4:
        return formData.engagementType && formData.primaryGoals.length > 0;
      default:
        return true;
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/30 to-white flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl border border-border p-12 text-center max-w-lg"
        >
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle weight="fill" className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-display text-2xl text-foreground mb-3">Welcome Aboard!</h1>
          <p className="text-muted-foreground mb-6">
            Thank you for completing your onboarding information. Our team will review your submission 
            and reach out within 24 hours to schedule your kickoff meeting.
          </p>
          <div className="bg-muted/50 rounded-xl p-4 text-left mb-6">
            <h3 className="font-semibold text-foreground mb-2">What's Next?</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                Review of your information by our team
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                Kickoff meeting scheduled within 24 hours
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                Welcome package sent to your email
              </li>
            </ul>
          </div>
          <Link to="/" className="btn-primary inline-flex items-center gap-2">
            Return to Homepage
            <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="client-onboarding-page" className="min-h-screen bg-gradient-to-b from-muted/30 to-white">
      {/* Header */}
      <section className="py-12 bg-hero-gradient">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge badge-primary mb-4">
              <Rocket weight="fill" className="w-4 h-4 mr-2" />
              New Client Onboarding
            </span>
            <h1 className="font-display text-3xl md:text-4xl text-foreground mb-3">
              Welcome to Noble Healthcare
            </h1>
            <p className="text-muted-foreground">
              Complete this form to help us prepare for your engagement
            </p>
          </motion.div>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-6 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg border border-border p-4">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <div className={`flex items-center gap-2 ${
                  currentStep >= step.num ? "text-primary" : "text-muted-foreground"
                }`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    currentStep > step.num 
                      ? "bg-primary text-white" 
                      : currentStep === step.num 
                        ? "bg-primary/10 text-primary border-2 border-primary"
                        : "bg-muted text-muted-foreground"
                  }`}>
                    {currentStep > step.num ? (
                      <CheckCircle weight="fill" className="w-5 h-5" />
                    ) : (
                      <step.icon weight="duotone" className="w-5 h-5" />
                    )}
                  </div>
                  <span className="hidden md:block text-sm font-medium">{step.title}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-12 md:w-24 h-1 mx-2 rounded ${
                    currentStep > step.num ? "bg-primary" : "bg-muted"
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-2xl shadow-lg border border-border p-8"
          >
            {/* Step 1: Organization Info */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground mb-6">Organization Information</h2>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Organization Name *</label>
                  <input
                    type="text"
                    value={formData.organizationName}
                    onChange={(e) => handleChange("organizationName", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    placeholder="e.g., Comfort Care Hospice"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Street Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">City *</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">State *</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleChange("state", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">ZIP</label>
                    <input
                      type="text"
                      value={formData.zip}
                      onChange={(e) => handleChange("zip", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Website</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleChange("website", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                      placeholder="https://"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Key Contacts */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground mb-6">Key Contacts</h2>
                
                <div className="bg-muted/30 rounded-xl p-4 mb-4">
                  <h3 className="font-semibold text-foreground mb-4">Primary Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
                      <input
                        type="text"
                        value={formData.primaryContact}
                        onChange={(e) => handleChange("primaryContact", e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Role/Title</label>
                      <input
                        type="text"
                        value={formData.primaryRole}
                        onChange={(e) => handleChange("primaryRole", e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                        placeholder="e.g., Administrator, DON"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
                      <input
                        type="email"
                        value={formData.primaryEmail}
                        onChange={(e) => handleChange("primaryEmail", e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Phone</label>
                      <input
                        type="tel"
                        value={formData.primaryPhone}
                        onChange={(e) => handleChange("primaryPhone", e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 rounded-xl p-4">
                  <h3 className="font-semibold text-foreground mb-4">Billing Contact (if different)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Full Name</label>
                      <input
                        type="text"
                        value={formData.billingContact}
                        onChange={(e) => handleChange("billingContact", e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                      <input
                        type="email"
                        value={formData.billingEmail}
                        onChange={(e) => handleChange("billingEmail", e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Organization Profile */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground mb-6">Organization Profile</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Average Daily Census *</label>
                    <input
                      type="number"
                      value={formData.censusAverage}
                      onChange={(e) => handleChange("censusAverage", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Total Employees</label>
                    <input
                      type="number"
                      value={formData.employeeCount}
                      onChange={(e) => handleChange("employeeCount", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Years in Operation</label>
                    <input
                      type="number"
                      value={formData.yearsInOperation}
                      onChange={(e) => handleChange("yearsInOperation", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Accreditation</label>
                    <select
                      value={formData.accreditation}
                      onChange={(e) => handleChange("accreditation", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    >
                      <option value="">Select...</option>
                      <option value="chap">CHAP</option>
                      <option value="achc">ACHC</option>
                      <option value="jointcommission">Joint Commission</option>
                      <option value="none">None</option>
                      <option value="seeking">Seeking Accreditation</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">EMR System</label>
                  <input
                    type="text"
                    value={formData.emrSystem}
                    onChange={(e) => handleChange("emrSystem", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    placeholder="e.g., MatrixCare, Axxess, WellSky"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Project Details */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground mb-6">Project Details</h2>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Engagement Type *</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {engagementTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleChange("engagementType", type)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          formData.engagementType === type
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Primary Goals * (select all that apply)</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {goalOptions.map((goal) => (
                      <button
                        key={goal}
                        type="button"
                        onClick={() => handleGoalToggle(goal)}
                        className={`p-3 rounded-xl border-2 text-left transition-all flex items-center gap-2 ${
                          formData.primaryGoals.includes(goal)
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <div className={`w-5 h-5 rounded flex items-center justify-center ${
                          formData.primaryGoals.includes(goal) ? "bg-primary" : "border border-border"
                        }`}>
                          {formData.primaryGoals.includes(goal) && (
                            <CheckCircle weight="fill" className="w-4 h-4 text-white" />
                          )}
                        </div>
                        {goal}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Additional Notes</label>
                  <textarea
                    value={formData.additionalNotes}
                    onChange={(e) => handleChange("additionalNotes", e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 resize-none"
                    placeholder="Any specific challenges, priorities, or information you'd like us to know..."
                  />
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-border">
              {currentStep > 1 ? (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="btn-secondary"
                >
                  Back
                </button>
              ) : (
                <div />
              )}
              
              {currentStep < 4 ? (
                <button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!canProceed()}
                  className="btn-primary flex items-center gap-2"
                >
                  Continue
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!canProceed() || loading}
                  className="btn-primary flex items-center gap-2"
                >
                  {loading ? "Submitting..." : "Complete Onboarding"}
                  <CheckCircle weight="bold" className="w-5 h-5" />
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ClientOnboardingPage;
